import React from "react";
import { Helmet } from "react-helmet";
import { Heading } from "../../components";
import TopMenuBar from "components/TopMenuBar";
import FooterSection from "components/FooterSection";

const features = [
    {
        id: "heartbeat",
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h3l3-9 4 18 3-9h5" />
            </svg>
        ),
        title: "Heartbeat & Rhythm Detection",
        tag: "rPPG · Contactless",
        description:
            "Measure heart rate and heart rate variability in real time from a standard camera feed — no wearables required. Our remote photoplethysmography (rPPG) model detects subtle skin-color fluctuations caused by blood flow, delivering medical-grade signal quality at 30+ fps.",
        bullets: [
            "Heart rate accuracy within ±3 BPM at 1–2 metres",
            "Works across skin tones and lighting conditions",
            "HRV metrics for stress and engagement scoring",
        ],
    },
    {
        id: "breath",
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7e60f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22V12" />
                <path d="M5 12c0-3.9 3.1-7 7-7s7 3.1 7 7" />
                <path d="M2 12c0-5.5 4.5-10 10-10s10 4.5 10 10" />
            </svg>
        ),
        title: "Breath Detection",
        tag: "Respiratory Rate · Passive",
        description:
            "Track breathing rate and depth from micro-movements in the face and upper chest region. No contact sensors, no IR arrays — just vision. Ideal for wellness applications, meditation guidance, and detecting stress-induced breath-holding.",
        bullets: [
            "Respiratory rate estimation (breaths per minute)",
            "Detects breath-hold and hyperventilation patterns",
            "Integrates with calming feedback loops in real time",
        ],
    },
    {
        id: "calmness",
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
        ),
        title: "Calmness Detection",
        tag: "Emotional State · Composite Signal",
        description:
            "Fuse heart rate, breathing rhythm, micro-expression analysis, and blink rate into a single real-time calmness score. Give your AI companions and game characters the ability to sense when a user is relaxed, anxious, or frustrated — and adapt accordingly.",
        bullets: [
            "0–100 calmness index updated every 500 ms",
            "Multi-signal fusion for robust accuracy",
            "Trigger adaptive NPC dialogue, pacing, or music",
        ],
    },
    {
        id: "eye-gesture",
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7e60f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        ),
        title: "Eye Gesture Recognition",
        tag: "Blink · Wink · Squint",
        description:
            "Detect intentional eye gestures — blinks, winks, extended squints, and double-blinks — as discrete input events. Enable hands-free UI control, accessibility triggers, and expressive avatar responses driven entirely by natural eye movement.",
        bullets: [
            "Single blink, double blink, and wink classification",
            "Squint intensity for analog expression mapping",
            "Sub-100 ms latency for responsive interaction",
        ],
    },
    {
        id: "gaze",
        icon: (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6ddcff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
            </svg>
        ),
        title: "Eye Look-at Location",
        tag: "Screen Gaze · 3D Scene",
        description:
            "Estimate where on screen — or in a 3D scene — the user is looking, using only a webcam. No expensive eye-tracking hardware. Map attention heatmaps, detect when a character has lost the player's focus, or drive gaze-aware NPC eye contact for dramatically more believable AI companions.",
        bullets: [
            "Screen-space gaze estimation at 60 fps",
            "3D look-direction vector for in-world targeting",
            "Attention heatmaps for UX analytics",
        ],
    },
];

const useCases = [
    { icon: "🎮", title: "Games & VR", body: "NPCs that read player stress and fatigue. Adaptive difficulty triggered by physiological state. Gaze-driven conversation systems." },
    { icon: "🤖", title: "AI Companions", body: "Companions that sense your mood without you saying a word. Calm you when you're stressed. Engage more when you're relaxed." },
    { icon: "🏥", title: "Health & Wellness", body: "Contactless biometric monitoring for meditation apps, telehealth check-ins, and mental wellness platforms." },
    { icon: "🏫", title: "EdTech", body: "Detect student attention and stress levels. Adapt lesson pacing automatically. Identify when focus is lost." },
];

const statStyle = {
    fontFamily: "Manrope, sans-serif",
};

export default function AIPerceptionPage() {
    return (
        <>
            <Helmet>
                <title>AI Perception SDK — AIHumanity</title>
                <meta
                    name="description"
                    content="Contactless biometric perception for AI — detect heartbeat, breathing, calmness, eye gestures, and gaze from a standard camera. No wearables required."
                />
            </Helmet>

            <div className="w-full bg-black-900_02">
                <TopMenuBar />

                {/* ── Hero ── */}
                <section className="relative flex flex-col items-center justify-center text-center px-5 pt-[120px] pb-[100px] overflow-hidden">
                    {/* faint background rings */}
                    <svg className="absolute opacity-[0.04] pointer-events-none" style={{ top: -100, right: -100 }} width="600" height="600" viewBox="0 0 600 600">
                        <circle cx="300" cy="300" r="260" fill="none" stroke="#7e60f8" strokeWidth="80" />
                        <circle cx="300" cy="300" r="160" fill="none" stroke="#6ddcff" strokeWidth="30" />
                    </svg>

                    <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.15em", color: "#7e60f8", textTransform: "uppercase", marginBottom: "1.25rem", display: "block" }}>
                        AI Perception SDK
                    </span>

                    <Heading size="3xl" as="h1" className="!text-transparent bg-gradient5 bg-clip-text leading-tight max-w-[820px] mx-auto">
                        See What Your AI Cannot Feel
                    </Heading>

                    <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "1.2rem", color: "#a0a0b8", maxWidth: 640, margin: "1.75rem auto 0", lineHeight: 1.7 }}>
                        Real-time biometric perception from a standard RGB camera. No wearables. No specialised hardware.
                        Give your AI the ability to sense heartbeat, breath, calmness, and gaze — all from the human face.
                    </p>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "2.5rem", flexWrap: "wrap", justifyContent: "center" }}>
                        <a
                            href="https://developer.aihumanity.io"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                fontFamily: "Manrope, sans-serif",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                padding: "0.75rem 2rem",
                                borderRadius: 12,
                                background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
                                color: "#fff",
                                textDecoration: "none",
                                display: "inline-block",
                            }}
                        >
                            Get SDK Access
                        </a>
                        <a
                            href="https://developer.aihumanity.io/docs"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                fontFamily: "Manrope, sans-serif",
                                fontSize: "0.95rem",
                                fontWeight: 600,
                                padding: "0.75rem 2rem",
                                borderRadius: 12,
                                border: "1px solid #2a2a3a",
                                color: "#e0e0e0",
                                textDecoration: "none",
                                display: "inline-block",
                            }}
                        >
                            View Docs
                        </a>
                    </div>

                    {/* stats row */}
                    <div style={{ display: "flex", gap: "3rem", marginTop: "4rem", flexWrap: "wrap", justifyContent: "center" }}>
                        {[
                            { val: "< 100ms", label: "end-to-end latency" },
                            { val: "30+ fps", label: "real-time processing" },
                            { val: "5 signals", label: "from one camera" },
                            { val: "Model-agnostic", label: "works with any LLM" },
                        ].map(({ val, label }) => (
                            <div key={val} style={{ textAlign: "center", ...statStyle }}>
                                <div style={{ fontSize: "1.6rem", fontWeight: 700, background: "linear-gradient(90deg, #6ddcff, #7e60f8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{val}</div>
                                <div style={{ fontSize: "0.8rem", color: "#666", marginTop: 4, letterSpacing: "0.05em" }}>{label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Features ── */}
                <section className="px-5 pb-[100px] max-w-[1040px] mx-auto">
                    <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
                        <Heading size="xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text">
                            Five Perception Signals. One SDK.
                        </Heading>
                        <p style={{ fontFamily: "Manrope, sans-serif", color: "#888", marginTop: "0.75rem", fontSize: "1rem" }}>
                            Each signal is independently accessible or combined into a unified emotional state stream.
                        </p>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
                        {features.map((f) => (
                            <div
                                key={f.id}
                                style={{
                                    background: "linear-gradient(145deg, #0d0d14, #11111e)",
                                    border: "1px solid #1e1e2e",
                                    borderRadius: 16,
                                    padding: "2rem",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                {/* top accent line */}
                                <div style={{ position: "absolute", top: 0, left: "2rem", right: "2rem", height: 2, background: "linear-gradient(90deg, #6ddcff, #7e60f8)", borderRadius: 1 }} />

                                <div style={{ width: 48, height: 48, borderRadius: "50%", border: "1px solid #2a2a3a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem" }}>
                                    {f.icon}
                                </div>

                                <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "#7e60f8", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                                    {f.tag}
                                </span>

                                <Heading size="md" as="h3" className="mt-2 mb-3">
                                    {f.title}
                                </Heading>

                                <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.9rem", color: "#a0a0b8", lineHeight: 1.7, marginBottom: "1.25rem" }}>
                                    {f.description}
                                </p>

                                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                    {f.bullets.map((b) => (
                                        <li key={b} style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.85rem", color: "#c0c0d0", display: "flex", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.4rem" }}>
                                            <span style={{ color: "#6ddcff", marginTop: 2, flexShrink: 0 }}>›</span>
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Use cases ── */}
                <section style={{ background: "linear-gradient(180deg, #0a0a12, #0d0d14)", padding: "80px 20px" }}>
                    <div className="max-w-[1040px] mx-auto">
                        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                            <Heading size="xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text">
                                Built for Every Emotional Interface
                            </Heading>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1.25rem" }}>
                            {useCases.map((u) => (
                                <div key={u.title} style={{ background: "#0d0d18", border: "1px solid #1e1e2e", borderRadius: 14, padding: "1.75rem" }}>
                                    <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{u.icon}</div>
                                    <Heading size="s" as="h4" className="mb-2">{u.title}</Heading>
                                    <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.875rem", color: "#888", lineHeight: 1.65 }}>{u.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── How it integrates ── */}
                <section className="max-w-[1040px] mx-auto px-5 py-[80px]">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", alignItems: "center" }} className="md:block">
                        <div>
                            <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.15em", color: "#7e60f8", textTransform: "uppercase", display: "block", marginBottom: "1rem" }}>
                                Integration
                            </span>
                            <Heading size="xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text mb-4">
                                Drop-in SDK. Works with Any Stack.
                            </Heading>
                            <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "1rem", color: "#a0a0b8", lineHeight: 1.75, marginBottom: "1.5rem" }}>
                                AI Perception plugs into your existing pipeline via a lightweight SDK. Stream perception events as JSON over WebSocket, REST, or native callbacks.
                                Supports Unity, Unreal, Python, Node.js, and browser environments.
                            </p>
                            {[
                                "Single WebSocket stream for all perception signals",
                                "Configurable sample rate per signal type",
                                "On-device inference — no cloud round-trip required",
                                "GDPR-friendly: video never leaves the device",
                            ].map((item) => (
                                <div key={item} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", marginBottom: "0.6rem" }}>
                                    <span style={{ color: "#6ddcff", fontFamily: "Manrope, sans-serif", marginTop: 2 }}>✓</span>
                                    <span style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.9rem", color: "#c0c0d0" }}>{item}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ background: "#0d0d14", border: "1px solid #1e1e2e", borderRadius: 14, padding: "1.75rem", fontFamily: "monospace", fontSize: "0.8rem", color: "#6ddcff", lineHeight: 1.8 }}>
                            <div style={{ color: "#555", marginBottom: "0.5rem" }}>{"// Connect to perception stream"}</div>
                            <div><span style={{ color: "#7e60f8" }}>const</span> sdk <span style={{ color: "#888" }}>=</span> <span style={{ color: "#6ddcff" }}>AIPerception</span>.connect&#40;&#123;</div>
                            <div style={{ paddingLeft: "1.5rem" }}>apiKey<span style={{ color: "#888" }}>:</span> <span style={{ color: "#e0c070" }}>"YOUR_API_KEY"</span>,</div>
                            <div style={{ paddingLeft: "1.5rem" }}>signals<span style={{ color: "#888" }}>:</span> [<span style={{ color: "#e0c070" }}>"heartbeat"</span>, <span style={{ color: "#e0c070" }}>"calmness"</span>, <span style={{ color: "#e0c070" }}>"gaze"</span>]</div>
                            <div>&#125;&#41;;</div>
                            <div style={{ marginTop: "1rem" }}>sdk.<span style={{ color: "#6ddcff" }}>on</span>(<span style={{ color: "#e0c070" }}>"perception"</span>, <span style={{ color: "#7e60f8" }}>event</span> <span style={{ color: "#888" }}>=&gt;</span> &#123;</div>
                            <div style={{ paddingLeft: "1.5rem", color: "#aaa" }}>console.<span style={{ color: "#6ddcff" }}>log</span>(event.heartRate);   <span style={{ color: "#555" }}>// 72</span></div>
                            <div style={{ paddingLeft: "1.5rem", color: "#aaa" }}>console.<span style={{ color: "#6ddcff" }}>log</span>(event.calmness);   <span style={{ color: "#555" }}>// 0.84</span></div>
                            <div style={{ paddingLeft: "1.5rem", color: "#aaa" }}>console.<span style={{ color: "#6ddcff" }}>log</span>(event.gaze);       <span style={{ color: "#555" }}>// &#123;x: 0.51, y: 0.48&#125;</span></div>
                            <div>&#125;&#41;;</div>
                        </div>
                    </div>
                </section>

                {/* ── CTA ── */}
                <section style={{ textAlign: "center", padding: "80px 20px", borderTop: "1px solid #1e1e2e" }}>
                    <Heading size="2xl" as="h2" className="!text-transparent bg-gradient5 bg-clip-text max-w-[600px] mx-auto mb-6">
                        Ready to Give Your AI a Sense of Feeling?
                    </Heading>
                    <p style={{ fontFamily: "Manrope, sans-serif", color: "#888", fontSize: "1rem", marginBottom: "2rem" }}>
                        Request early access to the AI Perception SDK and start integrating biometric signals today.
                    </p>
                    <a
                        href="https://developer.aihumanity.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "1rem",
                            fontWeight: 600,
                            padding: "0.9rem 2.5rem",
                            borderRadius: 14,
                            background: "linear-gradient(135deg, #6ddcff, #7e60f8)",
                            color: "#fff",
                            textDecoration: "none",
                            display: "inline-block",
                        }}
                    >
                        Request Access →
                    </a>
                </section>

                <FooterSection />
            </div>
        </>
    );
}
